import { useOrganization } from '@clerk/react';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { RiTeamLine } from '@remixicon/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useIsOrgAdmin } from 'src/auth';
import Hint from 'src/components/atoms/Hint';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';
import { InviteMember } from './InviteMember';
import usePeopleColumns from './usePeopleColumns';
import usePeopleRows, { PeopleRow } from './usePeopleRows';

const STORAGE_KEY = 'people';

const rowSearchKeys: (keyof PeopleRow)[] = ['name', 'email', 'role'];

export default function People() {
  const isAdmin = useIsOrgAdmin();
  const flags = useFlags();

  const { organization: clerkOrg } = useOrganization();

  const maxMembers = clerkOrg?.maxAllowedMemberships || null;
  const canInvite = isAdmin && (!maxMembers || clerkOrg.membersCount < maxMembers);

  const apiRef = useGridApiRef();
  const enhancedGridState = useGridRows({ mode: 'local', storageKey: STORAGE_KEY, searchKeys: rowSearchKeys });

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        toolbarActions={
          maxMembers ? (
            <Hint title={`Your organization is limited to ${maxMembers} seats, contact support if you need more`}>
              <div className="flex items-center gap-2 mr-4">
                <span className="font-bold">{`${clerkOrg.membersCount} / ${maxMembers}`}</span>
                <RiTeamLine size={20} className="text-neutral-12" />
              </div>
            </Hint>
          ) : null
        }
        toolbarActionsRight={<InviteMember canInvite={canInvite} />}
      />
    ),
    [apiRef, enhancedGridState, maxMembers, canInvite, clerkOrg.membersCount]
  );

  const rows = usePeopleRows();
  const columns = usePeopleColumns();

  return (
    <DataGrid<PeopleRow>
      mode="local"
      rows={rows}
      gridKey="settings.team.people"
      storageKey={STORAGE_KEY}
      columns={columns}
      apiRef={apiRef}
      slots={{ toolbar }}
      enhancedGridState={enhancedGridState}
      variant="list"
      pagination
      disabledColumns={{ subsidiary: !flags.multipleSubsidiaries }}
    />
  );
}
